<template>
  <page>
    <sidebar slot="sidebar"/>
    <span slot="header">Media</span>
    <span slot="breadcrumbs">
      <b-breadcrumb :items="breadcrumbs"/>
    </span>
    <form-wrapper>
      <b-form @submit="onSubmit">
        <b-form-group>
          <label for="type">Type</label>
          <input
            id="type"
            :value="type"
            disabled
            class="form-control"
            type="text"/>
        </b-form-group>

        <b-form-group>
          <label for="name-input">Name</label>
          <b-form-input
            v-validate="'required'"
            id="name-input"
            :disabled="saving"
            :class="{ 'is-invalid' : errors.has('name') || apiErrors.name.length }"
            :value="fields.name === null ? media.name : fields.name"
            class="form-control"
            type="text"
            name="name"
            @input="setName"/>
          <validation-errors :validation-errors="[errors.first('name')]"/>
          <validation-errors v-if="!errors.has('name')" :validation-errors="apiErrors.name"/>
        </b-form-group>

        <template v-if="!fields.file">
          <b-form-group>
            <label v-if="type === 'video'">Current Thumbnail (click for video)</label>
            <label v-else>Current File</label>
            <b-button
              v-if="type === 'pdf'"
              variant="outline-secondary"
              class="pdf"
              @click="displayMedia(media.id)">
              <i class="fas fa-file-pdf fa-3x"></i>
            </b-button>
            <image-preview
              v-else
              :src="preview"
              @click.native="displayMedia()"/>
          </b-form-group>
        </template>

        <template v-if="type === 'image'">
          <b-form-group>
            <image-upload 
              ref="imageUploadRef"
              @fileForEdit="setFile"
              @fileName="setName"
              @cropImage="setCropImage"
              @imageUploadHasError="setImageUploadHasError"
            />
          </b-form-group>
        </template>

        <template v-if="type === 'video' || type === 'pdf'">
          <label for="file-input">Update File</label>
          <b-form-group>
            <input
              v-validate="`validateAnyFile|mimes:${acceptedMimes}`"
              id="file-input"
              :disabled="saving"
              :class="{ 'is-invalid' : errors.has('file') || apiErrors.file.length }"
              :accept="acceptedMimes"
              type="file"
              data-vv-name="file"
              name="file"
              @change="setFile"/>
            <validation-errors :validation-errors="[errors.first('file')]"/>
            <validation-errors v-if="!errors.has('file')" :validation-errors="apiErrors.file"/>
          </b-form-group>
        </template>

        <span v-if="type === 'video'">
          <label for="thumbnail-input">Video Thumbnail</label>
          <b-form-group>
            <input
              v-validate="'validateThumbnail'"
              id="thumbnail-input"
              :disabled="saving"
              :class="{ 'is-invalid' : errors.has('thumbnail') || apiErrors.thumbnail.length }"
              type="file"
              accept="image/png,image/jpeg"
              name="thumbnail"
              @change="setThumbnail"/>
            <validation-errors :validation-errors="[errors.first('thumbnail')]"/>
            <validation-errors v-if="!errors.has('thumbnail')" :validation-errors="apiErrors.thumbnail"/>
          </b-form-group>
        </span>

        <relate-modules
          :initial-modules="media.modules"
          :disabled="saving"
          namespace="media"/>

        <b-form-group class="form-buttons">
          <b-button :disabled="saving" variant="secondary" @click="$router.push({name: 'admin-media'})">
            Cancel
          </b-button>
          <b-button :disabled="disabled" type="submit" variant="primary">
            Confirm
            <span v-if="saving"><i class="fas fa-spinner fa-spin"></i></span>
          </b-button>
        </b-form-group>

        <uploading-video v-if="showVideoAlert"/>
        <form-success :success="success" class="form-alert">Media Updated!</form-success>
      </b-form>

    </form-wrapper>
  </page>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { mapMimeTypeToGeneralType } from '../../../../helpers'
import formSuccess from '../../../common/form/form-success'
import uploadingVideo from '../../../common/form/uploading-video'
import formWrapper from '../../../common/form/form-wrapper'
import imagePreview from '../../../common/image-preview'
import imageUpload from '../../../common/form/image-upload'
import relateModules from '../modules/relateModules'
import page from '../../../templates/page'
import sidebar from '../../../sidebar'
import validationErrors from '../../../common/form/validation-errors'

export default {
  components: {
    formSuccess,
    formWrapper,
    imagePreview,
    imageUpload,
    page,
    relateModules,
    sidebar,
    uploadingVideo,
    validationErrors
  },
  data () {
    return {
      breadcrumbs: [{
        text: 'Media',
        to: { name: 'admin-media' }
      },
      {
        text: 'Edit',
        active: true
      }],
      imageUploadHasError: false,
      cropImage: false
    }
  },
  computed: {
    ...mapState('media/forms/update', [
      'apiErrors',
      'fields',
      'success',
      'saving'
    ]),
    ...mapState('config', {
      apiConfig: 'data'
    }),
    ...mapState('media', {
      media: 'current'
    }),
    disabled () {
      return this.errors.any() || this.saving || this.imageUploadHasError
    },
    preview () {
      if (this.media && this.media.thumbnail) {
        return this.media.thumbnail.url
      } else if (this.media) {
        return this.media.url
      } else {
        return ''
      }
    },
    type () {
      return mapMimeTypeToGeneralType(this.media.mime_type)
    },
    acceptedMimes () {
      switch (this.media.mime_type) {
      case 'image/jpeg':
        return 'image/jpeg,image/png'
      case 'image/png':
        return 'image/jpeg,image/png'
      case 'application/pdf':
        return 'application/pdf'
      case 'video/mp4':
        return 'video/mp4'
      default:
        return null
      }
    },
    showVideoAlert () {
      return this.fields.file && this.type === 'video' && this.saving
    }
  },
  created () {
    this.$store.commit('media/forms/update/resetState')
  },
  methods: {
    ...mapActions({
      updateMedia: 'media/update'
    }),
    onSubmit (event) {
      event.preventDefault()
      this.$validator.validateAll().then((result) => {
        if (result) {
          this.updateMedia({
            media: this.media
          })
        }
      })
    },
    displayMedia () {
      window.open(this.media.url)
    },
    setName (event) {
      this.$store.commit('media/forms/update/setField', {
        field: 'name',
        value: event
      })
    },
    setFile (event) {
      let file
      if (event.target && event.target.files) {
        file = event.target.files[0]
      } else {
        file = event
      }

      this.$store.commit('media/forms/update/setField', {
        field: 'file',
        value: file
      })
    },
    setThumbnail (event) {
      this.$store.commit('media/forms/update/setField', {
        field: 'thumbnail',
        value: event.target.files[0]
      })
    },
    setImageUploadHasError(hasError) {
      this.imageUploadHasError = hasError
    },
    setCropImage (value) {
      this.cropImage = value
    }
  }
}
</script>

<style lang="scss" scoped>
@import 'assets/stylesheets/main.scss';

.pdf {
  width: 8rem;
  height: 6rem;
  display: flex;
  justify-content: center;
  align-items: center;
}

</style>



// WEBPACK FOOTER //
// src/components/pages/admin/media/edit.vue